import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

import { fmtMoney, fmtDateFull as fmtDate, timeAgo } from '../../utils/formatters'
import { CHECK, CROSS } from '../../utils/icons'

export default function NotificationsPage() {
  var { user, profile } = useAuth()
  var isOwner = profile?.role === 'owner'
  var isOffice = profile?.role === 'office_staff'
  var canApprove = isOwner || isOffice

  var [notifications, setNotifications] = useState([])
  var [pendingJobs, setPendingJobs] = useState([])
  var [loading, setLoading] = useState(true)
  var [filter, setFilter] = useState('all')

  var fetchData = useCallback(async function() {
    setLoading(true)
    var { data: notifs } = await supabase.from('notifications').select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100)
    setNotifications(notifs || [])

    if (canApprove) {
      var { data: pending } = await supabase.from('jobs').select('*, profiles!jobs_store_partner_id_fkey(full_name, store_name)')
        .eq('request_status', 'pending')
        .eq('business_id', 'pools')
        .order('created_at', { ascending: false })
      setPendingJobs(pending || [])
    }
    setLoading(false)
  }, [user.id, canApprove])

  useEffect(function() { fetchData() }, [fetchData])

  var approveRequest = async function(jobId) {
    await supabase.from('jobs').update({ request_status: 'confirmed' }).eq('id', jobId)
    // Mark related notification as read
    await supabase.from('notifications').update({ is_read: true }).eq('related_id', jobId).eq('type', 'store_request')
    // Notify store partner
    var job = pendingJobs.find(function(j) { return j.id === jobId })
    if (job && job.store_partner_id) {
      await supabase.from('notifications').insert({
        user_id: job.store_partner_id,
        type: 'request_approved',
        title: 'Install Request Approved',
        message: (job.client_name || 'Your install') + ' on ' + fmtDate(job.start_date) + ' has been confirmed.',
        link: '/store?request=' + jobId,
        related_id: jobId,
        business_id: 'pools'
      })
    }
    fetchData()
  }

  var declineRequest = async function(jobId) {
    if (!window.confirm('Decline this install request? The job will be cancelled.')) return
    await supabase.from('jobs').update({ request_status: 'declined', status: 'cancelled' }).eq('id', jobId)
    await supabase.from('notifications').update({ is_read: true }).eq('related_id', jobId).eq('type', 'store_request')
    var job = pendingJobs.find(function(j) { return j.id === jobId })
    if (job && job.store_partner_id) {
      await supabase.from('notifications').insert({
        user_id: job.store_partner_id,
        type: 'request_declined',
        title: 'Install Request Declined',
        message: (job.client_name || 'Your install') + ' on ' + fmtDate(job.start_date) + ' was not approved. Please contact us for alternative dates.',
        link: '/store?request=' + jobId,
        related_id: jobId,
        business_id: 'pools'
      })
    }
    fetchData()
  }

  var markRead = async function(id) {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id)
    setNotifications(function(prev) { return prev.map(function(n) { return n.id === id ? { ...n, is_read: true } : n }) })
  }

  var markAllRead = async function() {
    var unread = notifications.filter(function(n) { return !n.is_read })
    for (var i = 0; i < unread.length; i++) {
      await supabase.from('notifications').update({ is_read: true }).eq('id', unread[i].id)
    }
    setNotifications(function(prev) { return prev.map(function(n) { return { ...n, is_read: true } }) })
  }

  var clearAll = async function() {
    if (!window.confirm('Clear all notifications?')) return
    var read = notifications.filter(function(n) { return n.is_read })
    for (var i = 0; i < read.length; i++) {
      await supabase.from('notifications').delete().eq('id', read[i].id)
    }
    fetchData()
  }

  var filteredNotifs = notifications.filter(function(n) {
    if (filter === 'unread') return !n.is_read
    if (filter === 'requests') return n.type === 'store_request'
    if (filter === 'invoices') return n.type === 'overdue_invoice'
    return true
  })

  var typeIcons = {
    store_request: String.fromCodePoint(0x1F3EA),
    overdue_invoice: String.fromCodePoint(0x26A0),
    job_completed: String.fromCodePoint(0x2705),
    request_approved: String.fromCodePoint(0x2705),
    request_declined: String.fromCodePoint(0x274C)
  }

  var unreadCount = notifications.filter(function(n) { return !n.is_read }).length

  if (loading) return <div style={s.loading}>Loading...</div>

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <div style={s.title}>NOTIFICATIONS</div>
          <div style={s.subtitle}>{unreadCount} unread</div>
        </div>
        <div style={s.headerActions}>
          {unreadCount > 0 && <button onClick={markAllRead} style={s.actionBtn}>MARK ALL READ</button>}
          <button onClick={clearAll} style={{...s.actionBtn, color:'rgba(255,255,255,0.3)'}}>CLEAR READ</button>
        </div>
      </div>

      {/* Pending Install Requests */}
      {canApprove && pendingJobs.length > 0 && (
        <div style={s.pendingSection}>
          <div style={s.sectionTitle}>PENDING INSTALL REQUESTS ({pendingJobs.length})</div>
          {pendingJobs.map(function(job) {
            var storeName = job.profiles?.store_name || job.profiles?.full_name || 'Store Partner'
            return (
              <div key={job.id} style={s.requestCard}>
                <div style={{flex:1}}>
                  <div style={s.requestTitle}>{job.client_name || job.title || 'Untitled'}</div>
                  <div style={s.requestMeta}>{storeName} {String.fromCodePoint(0x2022)} {fmtDate(job.start_date)}</div>
                  <div style={s.requestMeta}>{job.site_address}</div>
                  {job.pool_size && <div style={s.requestMeta}>{(job.pool_shape || '').charAt(0).toUpperCase() + (job.pool_shape || '').slice(1)} {job.pool_size}</div>}
                  {job.notes && <div style={{fontSize:'11px',color:'rgba(255,255,255,0.25)',marginTop:'4px',whiteSpace:'pre-wrap'}}>{job.notes}</div>}
                </div>
                <div style={{textAlign:'right'}}>
                  {job.contract_value > 0 && <div style={s.requestAmount}>{fmtMoney(job.contract_value)}</div>}
                  <div style={s.requestActions}>
                    <button onClick={function() { approveRequest(job.id) }} style={s.approveBtn}>{CHECK} APPROVE</button>
                    <button onClick={function() { declineRequest(job.id) }} style={s.declineBtn}>{CROSS} DECLINE</button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Filter tabs */}
      <div style={s.filterRow}>
        {[{id:'all',label:'ALL'},{id:'unread',label:'UNREAD'},{id:'requests',label:'REQUESTS'},{id:'invoices',label:'INVOICES'}].map(function(f) {
          return <button key={f.id} onClick={function() { setFilter(f.id) }} style={{...s.filterBtn,...(filter === f.id ? s.filterBtnActive : {})}}>{f.label}</button>
        })}
      </div>

      {/* Notification list */}
      {filteredNotifs.length === 0 && <div style={s.empty}>No notifications to show.</div>}
      {filteredNotifs.map(function(n) {
        return (
          <div key={n.id} onClick={function() { if (!n.is_read) markRead(n.id) }} style={{...s.notifCard, backgroundColor: n.is_read ? 'transparent' : 'rgba(180,140,60,0.04)', borderLeft: n.is_read ? '3px solid transparent' : '3px solid #b48c3c'}}>
            <span style={s.notifIcon}>{typeIcons[n.type] || String.fromCodePoint(0x1F514)}</span>
            <div style={{flex:1}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{...s.notifTitle, fontWeight: n.is_read ? 400 : 600}}>{n.title}</div>
                <div style={s.notifTime}>{timeAgo(n.created_at)}</div>
              </div>
              <div style={s.notifMsg}>{n.message}</div>
            </div>
            {!n.is_read && <span style={s.unreadDot} />}
          </div>
        )
      })}
    </div>
  )
}

var s = {
  container:{fontFamily:"'Barlow',sans-serif",maxWidth:'100%'},
  loading:{padding:'60px',textAlign:'center',color:'rgba(255,255,255,0.3)',fontSize:'15px'},
  header:{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'28px',flexWrap:'wrap',gap:'16px'},
  title:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'28px',fontWeight:700,letterSpacing:'3px',color:'#ffffff'},
  subtitle:{fontSize:'15px',color:'rgba(255,255,255,0.4)',marginTop:'4px'},
  headerActions:{display:'flex',gap:'8px'},
  actionBtn:{background:'none',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'3px',padding:'8px 14px',color:'rgba(255,255,255,0.5)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  sectionTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'14px',fontWeight:600,letterSpacing:'2px',color:'#b48c3c',marginBottom:'12px'},

  // Pending requests
  pendingSection:{padding:'20px',backgroundColor:'rgba(180,140,60,0.06)',border:'1px solid rgba(180,140,60,0.2)',borderRadius:'4px',marginBottom:'24px'},
  requestCard:{display:'flex',gap:'16px',padding:'16px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',marginBottom:'6px',alignItems:'flex-start'},
  requestTitle:{fontSize:'15px',fontWeight:600,color:'#ffffff'},
  requestMeta:{fontSize:'12px',color:'rgba(255,255,255,0.35)',marginTop:'3px'},
  requestAmount:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'20px',fontWeight:700,color:'#b48c3c',marginBottom:'8px'},
  requestActions:{display:'flex',gap:'6px',marginTop:'4px'},
  approveBtn:{padding:'8px 14px',backgroundColor:'rgba(74,140,106,0.2)',border:'1px solid rgba(74,140,106,0.4)',borderRadius:'3px',color:'#4a8c6a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',cursor:'pointer'},
  declineBtn:{padding:'8px 14px',backgroundColor:'rgba(224,92,92,0.1)',border:'1px solid rgba(224,92,92,0.3)',borderRadius:'3px',color:'#e05c5c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',cursor:'pointer'},

  // Filters
  filterRow:{display:'flex',gap:'4px',marginBottom:'20px'},
  filterBtn:{padding:'8px 16px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  filterBtnActive:{backgroundColor:'rgba(180,140,60,0.15)',borderColor:'rgba(180,140,60,0.4)',color:'#b48c3c'},

  // Notifications
  empty:{padding:'40px',textAlign:'center',color:'rgba(255,255,255,0.2)',fontSize:'14px'},
  notifCard:{display:'flex',gap:'12px',padding:'14px 16px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer',alignItems:'flex-start'},
  notifIcon:{fontSize:'18px',marginTop:'2px'},
  notifTitle:{fontSize:'14px',color:'#ffffff'},
  notifMsg:{fontSize:'12px',color:'rgba(255,255,255,0.4)',marginTop:'3px'},
  notifTime:{fontSize:'10px',color:'rgba(255,255,255,0.2)',whiteSpace:'nowrap',marginLeft:'12px'},
  unreadDot:{width:'8px',height:'8px',borderRadius:'50%',backgroundColor:'#b48c3c',marginTop:'6px',flexShrink:0},
}
